import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';

import {Analytics} from '../../../../services/analytics';
import {useRunsData, useRunSetsQuery} from '../../../../state/runs/hooks';
import * as ViewHooks from '../../../../state/views/hooks';
import * as PanelActions from '../../../../state/views/panel/actions';
import * as PanelBankSectionConfigActions from '../../../../state/views/panelBankSectionConfig/actions';
import {Ref as SectionRef} from '../../../../state/views/panelBankSectionConfig/types';
import {LayedOutPanel} from '../../../../util/panelTypes';
import {useRampFlagMediaVideoSyncEnabled} from '../../../../util/rampFeatureFlags';
import {makeContext} from '../../../MakeContext/makeContext';
import {useSingleRunModeContext} from '../../../MultiRunWorkspace/SingleRunMode';
import {panelViews} from '../../../PanelBank/types';
import {MediaBrowserPanelConfig, Spec} from '../../../PanelMediaBrowser';
import {useBoundingBoxes} from '../../../PanelMediaBrowser/hooks/useBoundingBoxes';
import {useMediaSliderKey} from '../../../PanelMediaBrowser/hooks/useMediaSliderKey';
import {useSliderRanges} from '../../../PanelMediaBrowser/hooks/useSliderRanges';
import {useMediaCardTypes} from '../../../PanelMediaBrowser/useGetMediaCardTypes';
import {
  getConfigWithDefaults,
  useAllRuns,
  useConfigWithDefaults,
  useOverlays,
} from '../../../PanelMediaBrowser/useMediaPanelValues';
import {usePanelsSharedRefsContext} from '../../../Views/contexts/PanelsSharedRefsContext';
import {useSectionSettingsContext} from '../SectionSettingsContextProvider';
import {useWorkspaceSettingsContext} from '../WorkspaceSettingsContextProvider';
import {
  ContextProviderState,
  MediaSettingsDrawerPanelProps,
  SettingsLevel,
} from './types';

export const useMediaSettingsDrawer = ({
  panelRef,
  panelBankSectionConfigRef,
}: MediaSettingsDrawerPanelProps): ContextProviderState => {
  const panel = ViewHooks.usePart(panelRef);
  // This drawer is only supported for a single media panel which only allows a single media type.
  const mediaCardType = useMediaCardTypes(
    panel.viewType === panelViews.mediaBrowser
      ? (panel?.config as MediaBrowserPanelConfig)?.mediaKeys
      : undefined
  )?.[0];
  const updateConfig = ViewHooks.useViewAction(panelRef, (ref, configUpdate) =>
    PanelActions.updateConfig(ref, configUpdate, panelBankSectionConfigRef)
  );
  // This is needed because the `updateConfig` action doesn't support unsetting a field
  // since it just merges the update over the old one, so setting a field to undefined
  // doesn't work.
  const unsetConfigField = ViewHooks.useViewAction(
    panelRef,
    (ref, field: keyof MediaBrowserPanelConfig) => {
      const {[field]: _, ...newConfig} =
        panel.config as MediaBrowserPanelConfig; // casting here because we already checked the panel config type
      return PanelActions.setConfig(ref, newConfig, undefined);
    }
  );
  const {customRunColorsRef, runSetRefs, customRunNamesRef} =
    usePanelsSharedRefsContext();
  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});
  const config = panel.config as MediaBrowserPanelConfig;
  // We need to load data about the current media shown in the panel so that we can know what to default
  // certain settings to.
  const panelQuery = useMemo(
    () => Spec.transformQuery(query, config),
    [query, config]
  );
  // loading these defaults shouldn't block the settings drawer
  const {data, loading} = useRunsData(panelQuery);
  const {configWithDefaults, hasMasks, allBoxes, allMasks} =
    useConfigWithDefaults({
      data,
      query: panelQuery,
      config,
      customRunColors,
      pageQuery: query,
    });
  const boundingBoxes = useBoundingBoxes(
    allBoxes,
    config.mediaKeys,
    panelRef.id
  );
  const sliderRanges = useSliderRanges(config.mediaKeys, panelRef.id);
  const entityName = useParams<{entityName: string}>().entityName;
  const isMediaVideoSyncEnabled = useRampFlagMediaVideoSyncEnabled(entityName);

  return useMemo(
    () => ({
      config: configWithDefaults,
      configWithoutDefaults: config,
      hasMasks,
      hasBoundingBoxes: Object.keys(boundingBoxes).length > 0,
      loading,
      mediaCardTypes: mediaCardType ? [mediaCardType] : undefined,
      rampFlags: {
        // arbitrarily using one of the ramp flags since value takes all into account
        media_video_sync_enabled_user: isMediaVideoSyncEnabled,
      },
      updateConfig,
      unsetConfigField,
      allBoxes: boundingBoxes,
      sliderRanges,
      allMasks,
      settingsLevel: SettingsLevel.PANEL,
    }),
    [
      configWithDefaults,
      config,
      hasMasks,
      boundingBoxes,
      loading,
      mediaCardType,
      isMediaVideoSyncEnabled,
      updateConfig,
      unsetConfigField,
      sliderRanges,
      allMasks,
    ]
  );
};

function getNumberOfOverridesInPanels(panels: NonNullable<LayedOutPanel>[]) {
  return panels.reduce(
    (acc, panel) => {
      if (panel.viewType === panelViews.mediaBrowser) {
        const config = panel.config;
        Object.keys(config).forEach((key: string) => {
          const configKey = key as keyof MediaBrowserPanelConfig; // we already checked the panel type, and we know this is coming from a key of the config.
          if (config[configKey] != null) {
            acc[configKey] = (acc[configKey] ?? 0) + 1;
          }
        });
        return acc;
      }
      return acc;
    },
    {} as Partial<Record<keyof MediaBrowserPanelConfig, number>>
  );
}

function unsetConfigFieldInPanels(
  panels: NonNullable<LayedOutPanel>[],
  field: keyof MediaBrowserPanelConfig
) {
  return panels.map(panel => {
    if (panel.viewType === panelViews.mediaBrowser) {
      const {[field]: _, ...newConfig} =
        panel.config as MediaBrowserPanelConfig; // casting here because we already checked the panel config type
      return newConfig;
    }
    return panel.config;
  });
}

const useSectionOverrides = (sectionRef: SectionRef) => {
  const section = ViewHooks.usePart(sectionRef);
  const panelRefs = section.panelRefs;
  const panels = ViewHooks.useParts(panelRefs);
  const numberOfOverridesBelow = useMemo(
    () => getNumberOfOverridesInPanels(panels),
    [panels]
  );

  const dispatch = useDispatch();

  const clearOverridesBelow = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      return dispatch(
        PanelActions.setConfigs(
          panelRefs,
          unsetConfigFieldInPanels(panels, field)
        )
      );
    },
    [panelRefs, dispatch, panels]
  );
  return {numberOfOverridesBelow, clearOverridesBelow};
};

export const useSectionMediaSettingsDrawer = (): ContextProviderState => {
  const {mediaCardTypes, mediaKeys, sectionMediaSettings, sectionRef} =
    useSectionSettingsContext();
  const {customRunColorsRef, runSetRefs, customRunNamesRef} =
    usePanelsSharedRefsContext();
  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});
  const {isSingleRun} = useSingleRunModeContext();
  const config = sectionMediaSettings;
  const mediaSliderKey = useMediaSliderKey(config.mediaSliderKey);

  const configWithDefaults = getConfigWithDefaults(
    config,
    null, // there can be multiple media types in the section, so just stick with the default as if there were none. this is only used for the number of columns to default to
    mediaSliderKey,
    isSingleRun
  );
  // We need to load data about the current media shown in the panel so that we can know what to default
  // certain settings to.
  const panelQuery = useMemo(
    () => Spec.transformQuery(query, {mediaKeys}),
    [query, mediaKeys]
  );
  const {data, loading} = useRunsData(panelQuery);

  const allRuns = useAllRuns({
    data,
    query: panelQuery,
    customRunColors,
    pageQuery: query,
  });

  const {allMasks, hasMasks, allBoxes, hasBoxes} = useOverlays({
    allRuns,
    mediaKeys,
  });

  const updateConfig = ViewHooks.useViewAction(
    sectionRef,
    PanelBankSectionConfigActions.updateSectionMediaSettings
  );

  const unsetConfigField = ViewHooks.useViewAction(
    sectionRef,
    PanelBankSectionConfigActions.unsetSectionMediaSetting
  );

  const {numberOfOverridesBelow, clearOverridesBelow} =
    useSectionOverrides(sectionRef);

  const updateConfigAndLog = useCallback(
    (configUpdate: Partial<MediaBrowserPanelConfig>) => {
      updateConfig(configUpdate);
      Analytics.track('Update Section Media Settings', {
        configUpdate: JSON.stringify(configUpdate),
      });
    },
    [updateConfig]
  );

  const unsetConfigFieldAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      unsetConfigField(field);
      Analytics.track('Unset Section Media Setting', {field});
    },
    [unsetConfigField]
  );

  const clearOverridesBelowAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      clearOverridesBelow(field);
      Analytics.track('Clear Panel Media Setting Overrides From Section', {
        field,
        numberOfOverrides: numberOfOverridesBelow[field],
      });
    },
    [clearOverridesBelow, numberOfOverridesBelow]
  );

  const entityName = useParams<{entityName: string}>().entityName;
  const isMediaVideoSyncEnabled = useRampFlagMediaVideoSyncEnabled(entityName);

  return useMemo(
    () => ({
      config: configWithDefaults,
      configWithoutDefaults: config,
      hasMasks,
      hasBoundingBoxes: hasBoxes,
      loading,
      rampFlags: {
        media_video_sync_enabled_user: isMediaVideoSyncEnabled,
      },
      mediaCardTypes,
      updateConfig: updateConfigAndLog,
      unsetConfigField: unsetConfigFieldAndLog,
      allBoxes,
      // We'd have to fetch all the files for all the section's runs to determine the min and max values
      // for the slider for each of the masks. Since this is section level, let's just handle non-existing mins and maxes properly in ConfidenceSlider.tsx.
      sliderRanges: null,
      allMasks,
      numberOfOverridesBelow: {
        panel: numberOfOverridesBelow,
      },
      clearOverridesBelow: {
        panel: clearOverridesBelowAndLog,
      },
      settingsLevel: SettingsLevel.SECTION,
    }),
    [
      mediaCardTypes,
      configWithDefaults,
      config,
      hasBoxes,
      hasMasks,
      loading,
      allBoxes,
      allMasks,
      updateConfigAndLog,
      unsetConfigFieldAndLog,
      numberOfOverridesBelow,
      clearOverridesBelowAndLog,
      isMediaVideoSyncEnabled,
    ]
  );
};

export const useWorkspaceMediaSettingsDrawer = () => {
  const {
    media: {
      mediaCardTypes,
      mediaKeys,
      workspaceMediaSettings,
      updateWorkspaceMediaSettings,
      unsetWorkspaceMediaSetting,
    },
  } = useWorkspaceSettingsContext();

  const {
    customRunColorsRef,
    runSetRefs,
    customRunNamesRef,
    panelBankConfigRef,
  } = usePanelsSharedRefsContext();
  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});
  const {isSingleRun} = useSingleRunModeContext();
  const config = workspaceMediaSettings;
  const mediaSliderKey = useMediaSliderKey(config.mediaSliderKey);

  const configWithDefaults = getConfigWithDefaults(
    config,
    null, // there can be multiple media types in the workspace, so just stick with the default as if there were none. this is only used for the number of columns to default to
    mediaSliderKey,
    isSingleRun
  );
  // We need to load data about the current media shown in the panel so that we can know what to default
  // certain settings to.
  const panelQuery = useMemo(
    () => Spec.transformQuery(query, {mediaKeys}),
    [query, mediaKeys]
  );
  const {data, loading} = useRunsData(panelQuery);

  const allRuns = useAllRuns({
    data,
    query: panelQuery,
    customRunColors,
    pageQuery: query,
  });

  const {allMasks, hasMasks, allBoxes, hasBoxes} = useOverlays({
    allRuns,
    mediaKeys,
  });

  const sectionRefsMaybe =
    ViewHooks.usePartMaybe(panelBankConfigRef)?.sectionRefs;
  const sectionRefs = useMemo(() => sectionRefsMaybe ?? [], [sectionRefsMaybe]);
  const sections = ViewHooks.useParts(sectionRefs);
  const panelRefs = sections.map(section => section.panelRefs).flat();
  const panels = ViewHooks.useParts(panelRefs);

  const numberOfOverridesBelowInPanels = useMemo(
    () => getNumberOfOverridesInPanels(panels),
    [panels]
  );
  const dispatch = useDispatch();
  const clearOverridesBelowInPanels = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      return dispatch(
        PanelActions.setConfigs(
          panelRefs,
          unsetConfigFieldInPanels(panels, field)
        )
      );
    },
    [panelRefs, dispatch, panels]
  );

  const sectionsMediaSettings = sections.map(
    section => section.sectionSettings?.media ?? {}
  );

  const numberOfOverridesBelowInSections = useMemo(
    () =>
      sectionsMediaSettings.reduce(
        (acc, mediaSettings) => {
          Object.keys(mediaSettings).forEach(key => {
            const configKey = key as keyof MediaBrowserPanelConfig; // We're doing Object.keys on a MediaBrowserPanelConfig, so we know this is a valid key.
            if (config[configKey] != null) {
              acc[configKey] = (acc[configKey] ?? 0) + 1;
            }
          });
          return acc;
        },
        {} as Partial<Record<keyof MediaBrowserPanelConfig, number>>
      ),
    [sectionsMediaSettings, config]
  );

  const clearOverridesBelowInSections = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      sectionRefs.forEach((sectionRef, i) => {
        if (sections[i].sectionSettings?.media?.[field] != null) {
          dispatch(
            PanelBankSectionConfigActions.unsetSectionMediaSetting(
              sectionRef,
              field
            )
          );
        }
      });
    },
    [sectionRefs, dispatch, sections]
  );

  const updateConfigAndLog = useCallback(
    (configUpdate: Partial<MediaBrowserPanelConfig>) => {
      updateWorkspaceMediaSettings(configUpdate);
      Analytics.track('Update Workspace Media Settings', {
        configUpdate: JSON.stringify(configUpdate),
      });
    },
    [updateWorkspaceMediaSettings]
  );

  const unsetConfigFieldAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      unsetWorkspaceMediaSetting(field);
      Analytics.track('Unset Workspace Media Setting', {field});
    },
    [unsetWorkspaceMediaSetting]
  );

  const clearPanelOverridesBelowAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      clearOverridesBelowInPanels(field);
      Analytics.track('Clear Panel Media Setting Overrides From Workspace', {
        field,
        numberOfOverrides: numberOfOverridesBelowInPanels[field],
      });
    },
    [clearOverridesBelowInPanels, numberOfOverridesBelowInPanels]
  );

  const clearSectionOverridesBelowAndLog = useCallback(
    (field: keyof MediaBrowserPanelConfig) => {
      clearOverridesBelowInSections(field);
      Analytics.track('Clear Section Media Setting Overrides From Workspace', {
        field,
        numberOfOverrides: numberOfOverridesBelowInSections[field],
      });
    },

    [clearOverridesBelowInSections, numberOfOverridesBelowInSections]
  );

  const entityName = useParams<{entityName: string}>().entityName;
  const isMediaVideoSyncEnabled = useRampFlagMediaVideoSyncEnabled(entityName);

  return useMemo(
    () => ({
      config: configWithDefaults,
      configWithoutDefaults: {}, // Product decision that we don't want to show the reset button for workspace-level settings.
      hasMasks,
      hasBoundingBoxes: hasBoxes,
      loading,
      rampFlags: {
        media_video_sync_enabled_user: isMediaVideoSyncEnabled,
      },
      mediaCardTypes,
      updateConfig: updateConfigAndLog,
      unsetConfigField: unsetConfigFieldAndLog,
      allBoxes,
      // We'd have to fetch all the files for all the workspace's runs to determine the min and max values
      // for the slider for each of the masks. Since this is workspace level, let's just handle non-existing mins and maxes properly in ConfidenceSlider.tsx.
      sliderRanges: null,
      allMasks,
      numberOfOverridesBelow: {
        panel: numberOfOverridesBelowInPanels,
        section: numberOfOverridesBelowInSections,
      },
      clearOverridesBelow: {
        panel: clearPanelOverridesBelowAndLog,
        section: clearSectionOverridesBelowAndLog,
      },
      settingsLevel: SettingsLevel.WORKSPACE,
    }),
    [
      mediaCardTypes,
      configWithDefaults,
      hasBoxes,
      hasMasks,
      loading,
      allBoxes,
      allMasks,
      updateConfigAndLog,
      unsetConfigFieldAndLog,
      numberOfOverridesBelowInPanels,
      numberOfOverridesBelowInSections,
      clearPanelOverridesBelowAndLog,
      clearSectionOverridesBelowAndLog,
      isMediaVideoSyncEnabled,
    ]
  );
};

export const {
  Provider: MediaSettingsDrawerContextProvider,
  hook: useMediaSettingsDrawerContext,
} = makeContext<ReturnType<typeof useMediaSettingsDrawer>>({
  contextType: 'MediaSettingsDrawer',
});
